/**
 * Time Granularity configuration object
 * Acts like the single source of truth for all time granularity related logic
 *
 * @private
 */
const timeGranularityConfig = {
    daily: {
        name: "Daily",
        symbol: "D",
        symbolTranslationKey: "timegranularity.day.symbol",
    },
    weekly: {
        name: "Weekly",
        symbol: "W",
        symbolTranslationKey: "timegranularity.week.symbol",
    },
    monthly: {
        name: "Monthly",
        symbol: "M",
        symbolTranslationKey: "timegranularity.month.symbol",
    },
} as const;

type TimeGranularityConfig = typeof timeGranularityConfig;
type TimeGranularityKeys = keyof TimeGranularityConfig;

// -------------------------------------------------------------------------------------------------
// Time Granularity Name
// -------------------------------------------------------------------------------------------------
export type TimeGranularityName = TimeGranularityConfig[TimeGranularityKeys]["name"];

/**
 * Canonical representation of time granularity: stable string keys (`Daily`, `Weekly`, `Monthly`) for
 * typed app logic, filters, and widgets. Do not use these as UI copy—labels come from translation keys
 * (e.g. {@link getTimeGranularitySymbolTranslationKey}).
 */
export const TIME_GRANULARITY_NAMES: TimeGranularityName[] = Object.values(timeGranularityConfig).map(
    ({ name }) => name,
);

export const isTimeGranularityName = (value: unknown): value is TimeGranularityName => {
    return typeof value === "string" && TIME_GRANULARITY_NAMES.includes(value as TimeGranularityName);
};

type TimeGranularityNameToSymbol = {
    [K in TimeGranularityKeys as TimeGranularityConfig[K]["name"]]: TimeGranularityConfig[K]["symbol"];
};

const timeGranularityNameToSymbol = Object.fromEntries(
    Object.values(timeGranularityConfig).map((entry) => [entry.name, entry.symbol]),
) as TimeGranularityNameToSymbol;

/**
 * Converts a canonical name to the short symbol (`D`, `W`, `M`) for internal wiring and for persisting
 * time granularity in the browser URL. Unknown names fall back to `"M"`.
 */
export const toTimeGranularitySymbol = (name: TimeGranularityName | (string & {})): TimeGranularitySymbol => {
    if (!isTimeGranularityName(name)) {
        return "M";
    }

    return timeGranularityNameToSymbol[name];
};

export const getTimeGranularityNameByIndex = (index: number): TimeGranularityName => {
    return TIME_GRANULARITY_NAMES[index] ?? "Monthly";
};

// -------------------------------------------------------------------------------------------------
// Time Granularity Symbol
// -------------------------------------------------------------------------------------------------
export type TimeGranularitySymbol = TimeGranularityConfig[TimeGranularityKeys]["symbol"];

/**
 * Short single-letter symbols (`D`, `W`, `M`) for internal use and for storing time granularity in the
 * browser URL. Not sent to or received from backend APIs. Canonical names live in {@link TIME_GRANULARITY_NAMES}.
 */
export const TIME_GRANULARITY_SYMBOLS: TimeGranularitySymbol[] = Object.values(timeGranularityConfig).map(
    ({ symbol }) => symbol,
);

export const isTimeGranularitySymbol = (value: string | undefined): value is TimeGranularitySymbol => {
    return typeof value === "string" && TIME_GRANULARITY_SYMBOLS.includes(value as TimeGranularitySymbol);
};

type TimeGranularitySymbolToName = {
    [K in TimeGranularityKeys as TimeGranularityConfig[K]["symbol"]]: TimeGranularityConfig[K]["name"];
};

const timeGranularitySymbolToName = Object.fromEntries(
    Object.values(timeGranularityConfig).map((entry) => [entry.symbol, entry.name]),
) as TimeGranularitySymbolToName;

/**
 * Converts a short symbol (`D`, `W`, `M`) from the URL or internal state into the canonical name
 * (`Daily`, `Weekly`, `Monthly`) for typed logic. Not for API payloads.
 */
export const toTimeGranularityName = (symbol: TimeGranularitySymbol & (string & {})): TimeGranularityName => {
    if (!isTimeGranularitySymbol(symbol)) {
        return "Monthly";
    }

    return timeGranularitySymbolToName[symbol];
};

export const getTimeGranularitySymbolByIndex = (index: number): TimeGranularitySymbol => {
    return TIME_GRANULARITY_SYMBOLS[index] ?? "M";
};

// -------------------------------------------------------------------------------------------------
// Translations
// -------------------------------------------------------------------------------------------------
type TimeGranularityNameToSymbolTranslationKey = {
    [K in TimeGranularityKeys as TimeGranularityConfig[K]["name"]]: TimeGranularityConfig[K]["symbolTranslationKey"];
};

const timeGranularitySymbolTranslationByName = Object.fromEntries(
    Object.values(timeGranularityConfig).map((entry) => [entry.name, entry.symbolTranslationKey]),
) as TimeGranularityNameToSymbolTranslationKey;

export const getTimeGranularitySymbolTranslationKey = (
    name: TimeGranularityName | (string & {}),
): string | undefined => {
    if (!isTimeGranularityName(name)) {
        return undefined;
    }

    return timeGranularitySymbolTranslationByName[name];
};
